"use client";

import { useEffect, useRef } from "react";
import { Headphones, Loader, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const contactMethods = [{
  icon: Mail,
  title: "Email us directly",
  detail: "hello@acumendb.com · project inquiries"
}, {
  icon: MessageSquare,
  title: "Design consultation",
  detail: "Schedule a site visit · 24/7 availability"
}, {
  icon: Headphones,
  title: "Call our office",
  detail: "865-555-0123 · Mon-Fri 9am-6pm"
}];
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg border border-border bg-secondary p-8 text-center">
        <p className="font-default text-lg font-medium text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Please share your name",
        minLength: {
          value: 2,
          message: "Name is too short"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-sm font-medium text-foreground">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="h-12 rounded-lg border-border bg-background px-4 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-4" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-sm font-medium text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="h-12 rounded-lg border-border bg-background px-4 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-4" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" rules={{
      required: "A short subject helps us route you"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-sm font-medium text-foreground">Project Type</FormLabel>
            <FormControl>
              <Input placeholder="New home, investment, or lot inquiry" className="h-12 rounded-lg border-border bg-background px-4 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-4" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Tell us a little about what you need",
      minLength: {
        value: 10,
        message: "A few more words, please"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-sm font-medium text-foreground">Message</FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Tell us about your vision..." className="min-h-[140px] rounded-lg border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-4" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree_terms" rules={{
      required: "Please agree to continue"
    }} render={({
      field
    }) => <FormItem>
            <label className="flex cursor-pointer items-start gap-3">
              <FormControl>
                <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} className="mt-1 size-4 cursor-pointer rounded border-border accent-primary" />
              </FormControl>
              <span className="font-default text-sm text-muted-foreground">
                I agree to the privacy policy. We respect your privacy and never share your data.
              </span>
            </label>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
          <p className="font-default text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="flex flex-col-reverse items-start justify-between gap-4 pt-2 sm:flex-row sm:items-center">
        <p className="font-default text-xs tracking-wide text-muted-foreground">
          We will respond within one business day.
        </p>
        <Button type="submit" disabled={isSubmitting} className="h-12 rounded-lg bg-primary px-7 font-default text-sm font-medium text-primary-foreground transition-all duration-300 hover:bg-primary/90">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Sending...
            </> : "Start Your Project"}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('[data-contact-item]')) as HTMLElement[];
    const reset = () => {
      els.forEach(el => {
        el.style.transition = "none";
        el.style.opacity = "0";
        el.style.visibility = "hidden";
        el.style.transform = "translateX(-40px)";
      });
    };
    const animate = () => {
      els.forEach((el, i) => {
        el.style.visibility = "visible";
        el.style.transition = `opacity 600ms cubic-bezier(0.76, 0, 0.24, 1) ${i * 100}ms, transform 600ms cubic-bezier(0.76, 0, 0.24, 1) ${i * 100}ms`;
        el.style.opacity = "1";
        el.style.transform = "translateX(0)";
      });
    };
    reset();
    let lastScrollY = window.scrollY;
    let animated = false;
    const handleScroll = () => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return;
      const rect = sentinel.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView && !animated) {
        animated = true;
        reset();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            animate();
          });
        });
      } else if (!inView && rect.top > 0) {
        // Sentinel is below viewport — scrolled back up, reset
        animated = false;
        reset();
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <section data-section-id="8886" id="begin-your-build" className="relative overflow-hidden bg-background py-24 md:py-32 text-foreground dark">
      <div className="relative mx-auto max-w-[1240px] px-6 md:px-8">
        <div ref={sentinelRef} style={{
        height: "1px",
        marginBottom: "-1px"
      }} aria-hidden="true" />
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">

          <div data-contact-item className="lg:col-span-5">
            <p className="font-default text-sm font-medium tracking-widest text-primary uppercase">
              Contact Us
            </p>
            <h2 className="mt-4 font-default text-4xl font-bold leading-[1.08] tracking-tight text-foreground md:text-5xl lg:text-[3.25rem]">
              Let's build your <span className="text-primary">vision</span>.
            </h2>
            <p className="mt-6 max-w-[40ch] font-default text-lg leading-relaxed text-muted-foreground">
              Ready to start your residential project in Knoxville? Reach out to discuss your site, budget, and design goals.
            </p>

            <div className="mt-10 space-y-5">
              {contactMethods.map((method, i) => {
              const Icon = method.icon;
              return <div key={i} data-contact-item className="flex items-start gap-4">
                    <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-secondary">
                      <Icon className="size-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="pt-1">
                      <h4 className="font-default text-base font-semibold text-foreground">
                        {method.title}
                      </h4>
                      <p className="mt-1 font-default text-xs tracking-wide text-muted-foreground">
                        {method.detail}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div data-contact-item className="lg:col-span-7">
            <div className="rounded-lg border border-border bg-background p-7 shadow-md md:p-10">
              <FormProvider formKey="build_form_home" formId="build_form_home" sectionName="reach-out" defaultValues={{
              name: "",
              email: "",
              subject: "",
              message: "",
              agree_terms: false
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              subject: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              },
              agree_terms: {
                required: true
              }
            }} submitText="Start Your Project" successMessage="Thank you. We have received your inquiry and will be in touch shortly." errorMessage="Something went wrong. Please try sending again.">
                <ReachOutForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}